/** Pure helpers for rendering FileEngineeringMapV1 fields in the UI. */

export function formatSymbolRef(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "object") {
    const name = value.name || value.symbol;
    if (typeof name === "string" && name.trim()) {
      if (value.isDefault === true) {
        return name === "default" ? "default" : `default: ${name.trim()}`;
      }
      return name.trim();
    }
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function formatRouteRef(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "object") {
    const method = String(value.method || "GET")
      .trim()
      .toUpperCase();
    const path = String(value.path || "").trim();
    if (path) return `${method} ${path}`;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function formatStringLike(value) {
  if (value == null) return null;
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed || null;
  }
  if (typeof value === "object") {
    for (const key of ["name", "path", "value", "symbol", "key"]) {
      if (typeof value[key] === "string" && value[key].trim()) {
        return value[key].trim();
      }
    }
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

export function normalizeDisplayList(values, formatter = formatStringLike, options = {}) {
  const { preserveOrder = false } = options;
  if (!Array.isArray(values)) return [];
  const seen = new Set();
  const out = [];
  for (const value of values) {
    const formatted = formatter(value);
    if (!formatted || seen.has(formatted)) continue;
    seen.add(formatted);
    out.push(formatted);
  }
  if (preserveOrder) return out;
  return out.sort((a, b) => a.localeCompare(b));
}

export function capList(items, max = 8) {
  const safe = Array.isArray(items) ? items.filter(Boolean) : [];
  const visible = safe.slice(0, max);
  const remaining = Math.max(0, safe.length - visible.length);
  return { visible, remaining, total: safe.length };
}

export function formatRiskLabel(riskLevel) {
  const normalized = String(riskLevel || "unknown").toLowerCase();
  if (normalized === "low") return "Low risk";
  if (normalized === "medium") return "Medium risk";
  if (normalized === "high") return "High risk";
  return "Unknown risk";
}

const FILE_TYPE_LABELS = {
  route: "Route",
  component: "Component",
  hook: "Hook",
  service: "Service",
  model: "Model",
  config: "Config",
  test: "Test",
  script: "Script",
  doc: "Docs",
  manifest: "Manifest",
  lib: "Lib",
  generated: "Generated",
  unknown: "Unknown",
};

const LANGUAGE_LABELS = {
  typescript: "TypeScript",
  javascript: "JavaScript",
  tsx: "TSX",
  jsx: "JSX",
  json: "JSON",
  markdown: "Markdown",
  md: "Markdown",
  python: "Python",
  go: "Go",
  rust: "Rust",
  java: "Java",
  yaml: "YAML",
  yml: "YAML",
};

export function formatFileTypeLabel(fileType) {
  const normalized = String(fileType || "unknown").trim().toLowerCase();
  if (!normalized) return "Unknown";
  return FILE_TYPE_LABELS[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function formatLanguageLabel(language) {
  const normalized = String(language || "").trim().toLowerCase();
  if (!normalized) return "";
  return LANGUAGE_LABELS[normalized] || normalized.charAt(0).toUpperCase() + normalized.slice(1);
}

export function getRiskSectionTitle(riskLevel) {
  const normalized = String(riskLevel || "unknown").toLowerCase();
  if (normalized === "high" || normalized === "medium") return "Risk";
  return "Behavior notes";
}

export function isElevatedRisk(riskLevel) {
  const normalized = String(riskLevel || "unknown").toLowerCase();
  return normalized === "high" || normalized === "medium";
}

export function hasEngineeringMap(summary) {
  return Boolean(summary?.engineeringMap && typeof summary.engineeringMap === "object");
}

export function resolvePurpose(summary, engineeringMap) {
  const purpose = String(engineeringMap?.purpose || "").trim();
  if (purpose) return purpose;
  return String(summary?.description || "").trim();
}
